import { useMutation } from "@apollo/client"
import { Check, Close } from "@mui/icons-material"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { default as DataGridComponent } from "components/DataGridComponent/DataGridComponent"
import { DeleteDialog } from "components/DeleteDialog/DeleteDialog"
import { PDFDropzone } from "components/Dropzone/PDFDropzone"
import { HaufeIcon } from "components/HaufeIcon/HaufeIcon"
import { TagMultiSelect, TagOption } from "components/TagFilters/TagMultiSelect"
import { DELETE_FILE, UPLOAD_FILE } from "graphql/queries"
import { FileType, FileUploadInput, Norm } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useEffect, useMemo, useState } from "react"
import { branchList, treoLocationList } from "utils/constants"
import { v4 } from "uuid"
import { getPDFUrl } from "../../../store/pdfService"
import { AddCalculation } from "../AdminExaminationConfig/AddCalculation"

// Define the ExamCalc type locally to match the structure used in Norm
type ExamCalc = {
  id: string
  name: string
  isAccredited: boolean
  location?: any
  examinationConfig: any
  calculation: any
  tags?: TagOption[]
}

type AddNormsDialogProps = {
  open: boolean
  onClose: () => void
  formState: Norm
  // eslint-disable-next-line no-unused-vars
  onSubmit: (arg: Norm, documents: { id: string; name: string }[]) => void
}

type ValidationErrors = {
  name?: string
  examinationCalculations?: { [index: number]: { name?: string } }
}

type NormFormState = Omit<Norm, "examinationCalculations"> & {
  examinationCalculations: ExamCalc[]
}

const ensureExamCalcTags = (examCalc: ExamCalc): ExamCalc => ({
  ...examCalc,
  tags: (examCalc.tags || []).map((tag) => ({
    id: tag.id,
    name: tag.name,
    isNew: tag.isNew,
  })),
})

const toFormState = (norm: Norm): NormFormState => ({
  ...norm,
  examinationCalculations: norm.examinationCalculations.map((calc: any) =>
    ensureExamCalcTags({ ...calc })
  ),
})

const toSubmitNorm = (norm: NormFormState): Norm =>
  ({
    ...norm,
    examinationCalculations: norm.examinationCalculations.map((calc) => ({
      ...calc,
      tags: (calc.tags || []).map((tag) => ({
        id: tag.id ?? "",
        name: tag.name,
      })),
    })),
  }) as unknown as Norm

export const AddNormsDialog = (props: AddNormsDialogProps) => {
  const { open, onClose, formState, onSubmit } = props
  const [pdfFiles, setPdfFiles] = useState<File[]>([])
  const [pdfBase64Strings, setPdfBase64Strings] = useState<string[]>([])
  const [filesToDelete, setFilesToDelete] = useState<string[]>([])
  const [calcToDelete, setCalcToDelete] = useState<string[]>([])
  const [localFormState, setLocalFormState] = useState<NormFormState>(toFormState(formState))
  const [autocompleteKey, setAutocompleteKey] = useState(0)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  const [editingExamCalc, setEditingExamCalc] = useState<ExamCalc | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  // TODO: Figure out PDF dialog implementation later

  const [uploadFile] = useMutation(UPLOAD_FILE)
  const [deleteFile] = useMutation(DELETE_FILE)

  const { examinationConfigs } = useStoreState((state) => state)

  const [fetchExaminationConfigs] = useStoreActions((actions) => [actions.fetchExaminationConfigs])

  const allTagOptions = useMemo(() => {
    const tagMap = new Map<string, TagOption>()
    localFormState.examinationCalculations.forEach((calc) => {
      ;(calc.tags || []).forEach((tag) => {
        const key = tag.name.toLowerCase()
        if (!tagMap.has(key)) {
          tagMap.set(key, { id: tag.id, name: tag.name })
        }
      })
    })
    return Array.from(tagMap.values())
  }, [localFormState.examinationCalculations])

  useEffect(() => {
    const loadExaminationConfigs = async () => {
      if (examinationConfigs.length === 0) {
        try {
          await fetchExaminationConfigs({ fresh: false })
        } catch (err) {
          console.error("Error loading examination configs:", err)
        }
      }
    }
    loadExaminationConfigs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const validateForm = (norm: NormFormState): ValidationErrors => {
    const errors: ValidationErrors = {}

    // Check required fields
    if (!norm.name?.trim()) {
      errors.name = "Name ist erforderlich"
    }

    // Validate examination calculations
    const examCalcErrors: { [index: number]: { name?: string } } = {}
    const usedNames = new Set<string>()

    norm.examinationCalculations.forEach((examCalc, index) => {
      if (!examCalc.name?.trim()) {
        const configName = examCalc.examinationConfig?.name || `Prüfverfahren ${index + 1}`
        examCalcErrors[index] = {
          name: `Name ist erforderlich (${configName})`,
        }
      } else if (usedNames.has(examCalc.name.trim().toLowerCase())) {
        // Include the examination config name to help user identify which one has the duplicate
        const configName = examCalc.examinationConfig?.name || `Prüfverfahren ${index + 1}`
        examCalcErrors[index] = {
          name: `Name muss eindeutig sein (${configName})`,
        }
      } else {
        usedNames.add(examCalc.name.trim().toLowerCase())
      }
    })

    if (Object.keys(examCalcErrors).length > 0) {
      errors.examinationCalculations = examCalcErrors
    }

    return errors
  }

  useEffect(() => {
    const normalized = toFormState(formState)
    setLocalFormState(normalized)
    setValidationErrors(validateForm(normalized)) // Clear errors when form state changes
  }, [formState])

  const handleFormChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target
    if (name) {
      setLocalFormState((prevState) => {
        const newState = { ...prevState, [name]: value }
        setValidationErrors(validateForm(newState))
        return newState
      })
    }
  }

  const handleFileSelect = (files: File[], base64Strings: string[]) => {
    setPdfFiles(files)
    setPdfBase64Strings(base64Strings)
  }
  const handleRemoveDocument = (docId: string) => {
    setLocalFormState((prevState) => ({
      ...prevState,
      documents: prevState.documents.filter((doc) => doc.id !== docId),
    }))
    setFilesToDelete((prev) => [...prev, docId])
  }

  const handleDeleteFiles = async () => {
    if (filesToDelete.length > 0) {
      try {
        await deleteFile({ variables: { ids: filesToDelete } })
      } catch (error) {
        console.error("Fehler beim Löschen der Datei:", error)
      }
      setFilesToDelete([])
    }
  }

  const handleUploadFiles = async () => {
    const newDocuments: { id: string; name: string }[] = []
    // Upload all files
    for (let i = 0; i < pdfFiles.length; i++) {
      const file = pdfFiles[i]
      const base64 = pdfBase64Strings[i]

      if (file && base64) {
        const fileInput: FileUploadInput = {
          base64: base64,
          createdAt: new Date().toISOString(),
          createdBy: "", // Remove user dependency for now
          filename: file.name,
          id: "NEW",
          name: file.name.split(".")[0],
          type: FileType.PDF,
        }

        try {
          const result = await uploadFile({ variables: { input: fileInput } })
          if (result.data) {
            newDocuments.push({ id: result.data.uploadFile, name: file.name })
          }
        } catch (error) {
          console.error("Fehler beim Hochladen der Datei:", error)
        }
      }
    }
    return newDocuments
  }

  const handleSubmit = async () => {
    const errors = validateForm(localFormState)
    setValidationErrors(errors)
    if (Object.keys(errors).length > 0) {
      return
    }

    await handleDeleteFiles()

    const newDocuments = await handleUploadFiles()

    // Combine existing documents with new ones
    const allDocuments = [
      ...localFormState.documents.map((doc) => ({
        id: doc.id,
        name: doc.name,
      })),
      ...newDocuments.map((doc) => ({
        id: doc.id,
        name: doc.name,
      })),
    ]
    onSubmit(toSubmitNorm(localFormState), allDocuments)
    setPdfFiles([])
    setPdfBase64Strings([])
  }

  const handleCancel = () => {
    setPdfFiles([])
    setPdfBase64Strings([])
    setLocalFormState(toFormState(formState))
    setValidationErrors({})
    onClose()
  }

  const handleDeleteExamCalc = (): void => {
    setLocalFormState((prevState) => ({
      ...prevState,
      examinationCalculations: prevState.examinationCalculations.filter(
        (examCalc) => !calcToDelete.includes(examCalc.id)
      ),
    }))
    setCalcToDelete([])
  }

  const handleEditExamCalc = (id: string) => {
    const examCalc = localFormState.examinationCalculations.find((ec) => ec.id === id)
    if (examCalc) {
      setEditingExamCalc({
        ...examCalc,
        tags: (examCalc.tags || []).map((tag) => ({ ...tag })),
      })
      setEditDialogOpen(true)
    }
  }

  const handleSaveExamCalc = (updatedExamCalc: ExamCalc) => {
    setLocalFormState((prevState) => {
      const newState = {
        ...prevState,
        examinationCalculations: prevState.examinationCalculations.map((item) =>
          item.id === updatedExamCalc.id ? updatedExamCalc : item
        ),
      }
      setValidationErrors(validateForm(newState))
      return newState
    })
    setEditDialogOpen(false)
    setEditingExamCalc(null)
  }

  const examCalcColumns = [
    {
      field: "PV#",
      headerName: "PV#",
      flex: 0.2,
      renderCell: (params: any) => localFormState.examinationCalculations.indexOf(params.row) + 1,
    },
    { field: "name", headerName: "Name", flex: 2 },
    {
      field: "tags",
      headerName: "Tags",
      flex: 2,
      renderCell: (params: any) => {
        const tags: TagOption[] = params.row.tags || []
        if (!tags.length) {
          return (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          )
        }
        return (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
            {tags.map((tag) => (
              <Chip key={`${params.row.id}-${tag.name}`} label={tag.name} size="small" />
            ))}
          </Stack>
        )
      },
    },
    {
      field: "examinationConfig",
      headerName: "Prüfverfahren",
      flex: 2,
      renderCell: (params: any) => params.value?.name || "",
    },
    {
      field: "location",
      headerName: "Standort",
      flex: 0.7,
      renderCell: (params: any) => params.value?.name || "",
    },
    {
      field: "isAccredited",
      headerName: "Akkr. / Harmon.",
      flex: 0.5,
      renderCell: (params: any) => {
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {params.row.isAccredited ? <Check color="success" /> : <Close color="error" />}
            <Divider orientation="vertical" flexItem />
            {params.row.isHarmonized ? <Check color="success" /> : <Close color="error" />}
          </Box>
        )
      },
    },
  ]

  return (
    <Dialog open={open} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        {localFormState.id === "NEW" ? "Norm hinzufügen" : "Norm bearbeiten"}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
            <Typography variant="subtitle1">Allgmeines</Typography>

            {Object.keys(validationErrors).length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                  Bitte korrigieren Sie die folgenden Fehler:
                </Typography>
                <Stack spacing={0.5}>
                  {Object.entries(validationErrors).map(([field, error]) => {
                    if (field === "examinationCalculations" && typeof error === "object") {
                      return Object.entries(error).map(([index, examError]) => (
                        <Typography key={`${field}-${index}`} variant="body2">
                          • Prüfverfahren {parseInt(index) + 1}: {examError.name}
                        </Typography>
                      ))
                    }
                    return (
                      <Typography key={field} variant="body2">
                        • {error as string}
                      </Typography>
                    )
                  })}
                </Stack>
              </Alert>
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                mt: 1,
                gap: 2,
              }}
            >
              <TextField
                size="small"
                name="name"
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Bezeichnung* <HaufeIcon />
                  </Box>
                }
                type="text"
                fullWidth
                value={localFormState.name}
                onChange={handleFormChange}
                error={!!validationErrors.name}
                helperText={validationErrors.name}
              />
              <TextField
                size="small"
                name="label"
                label="Titel"
                type="text"
                fullWidth
                value={localFormState.label}
                onChange={handleFormChange}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                mt: 1,
                gap: 2,
              }}
            >
              <TextField
                size="small"
                name="date"
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Jahr/Ausgabe <HaufeIcon />
                  </Box>
                }
                type="text"
                fullWidth
                value={localFormState.date}
                onChange={handleFormChange}
              />
              <TextField
                size="small"
                name="revision"
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Revision <HaufeIcon />
                  </Box>
                }
                type="text"
                fullWidth
                value={localFormState.revision}
                onChange={handleFormChange}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                mt: 1,
                alignItems: "center",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Autocomplete
                  fullWidth
                  options={branchList}
                  getOptionLabel={(option) => option}
                  renderInput={(params) => <TextField {...params} label="Branche" />}
                  value={localFormState.branch}
                  onChange={(_, value) => {
                    setLocalFormState((prevState) => {
                      const newState = {
                        ...prevState,
                        branch: value as string,
                      }
                      setValidationErrors(validateForm(newState))
                      return newState
                    })
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                  flex: 1,
                  width: "100%",
                  justifyContent: "center", // Center the checkboxes
                }}
              >
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={localFormState.isOutdated}
                        onChange={(e) =>
                          setLocalFormState((prevState) => {
                            const newState = {
                              ...prevState,
                              isOutdated: e.target.checked,
                            }
                            setValidationErrors(validateForm(newState))
                            return newState
                          })
                        }
                      />
                    }
                    label="Veraltet"
                  />
                </FormGroup>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={localFormState.isHarmonized}
                        onChange={(e) =>
                          setLocalFormState((prevState) => {
                            const newState = {
                              ...prevState,
                              isHarmonized: e.target.checked,
                            }
                            setValidationErrors(validateForm(newState))
                            return newState
                          })
                        }
                      />
                    }
                    label="Harmonisiert"
                  />
                </FormGroup>
              </Box>
            </Box>
            {localFormState.documents.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Vorhandene Dokumente
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {localFormState.documents.map((doc) => (
                    <Chip
                      key={`existing-doc-${doc.id}`}
                      icon={<PictureAsPdfIcon />}
                      label={doc.name}
                      onClick={async () => {
                        try {
                          const pdfUrl = await getPDFUrl(doc.id)
                          window.open(pdfUrl, "_blank")
                        } catch (error) {
                          console.error("Error opening PDF:", error)
                          // You might want to show a user-friendly error message here
                        }
                      }}
                      onDelete={() => handleRemoveDocument(doc.id)}
                    />
                  ))}
                </Box>
                <Divider sx={{ my: 1 }} />
              </>
            )}

            <Typography variant="subtitle1">Neue Dokumente hinzufügen</Typography>
            <PDFDropzone onFileSelect={handleFileSelect} selectedFiles={pdfFiles} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
            <Typography variant="subtitle1">Prüfverfahren hinzufügen</Typography>
            {examinationConfigs.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 4,
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Keine Prüfverfahren-Konfigurationen verfügbar
                </Typography>
              </Box>
            ) : (
              <Autocomplete
                key={autocompleteKey}
                options={examinationConfigs}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => <TextField {...params} label="Prüfverfahren hinzufügen" />}
                onChange={(_, value) => {
                  if (value) {
                    // Generate a unique default name based on the examination config name
                    const existingNames = new Set(
                      localFormState.examinationCalculations.map((ec) =>
                        ec.name?.trim().toLowerCase()
                      )
                    )
                    const baseName = value.name.trim()
                    let uniqueName = baseName
                    let counter = 2
                    while (existingNames.has(uniqueName.toLowerCase())) {
                      uniqueName = `${baseName} (${counter})`
                      counter++
                    }

                    const newState = {
                      ...localFormState,
                      examinationCalculations: [
                        ...localFormState.examinationCalculations,
                        {
                          id: v4(),
                          name: uniqueName,
                          isAccredited: true,
                          location: value.location || treoLocationList[0],
                          examinationConfig: value,
                          calculation: {
                            ...value.defaultCalculation,
                            id: "NEW",
                          },
                          tags: [],
                        },
                      ],
                    }
                    setLocalFormState(newState)
                    setValidationErrors(validateForm(newState))
                    setAutocompleteKey((prev) => prev + 1)
                  }
                }}
                clearOnBlur={false}
              />
            )}
          </Box>
        </Box>

        {/* Full width Prüfverfahren & Kalkulation section */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <Typography variant="subtitle1">Prüfverfahren & Kalkulation</Typography>

          <DataGridComponent
            columns={examCalcColumns}
            rows={localFormState.examinationCalculations}
            getRowId={(row) => row.id}
            onEdit={handleEditExamCalc}
            onDelete={setCalcToDelete}
            isNoToolbar={false}
            enablePagination
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <StyledButton buttonType="cancel" onClick={handleCancel} variant="contained" />
        <StyledButton
          buttonType="save"
          onClick={handleSubmit}
          disabled={Object.keys(validationErrors).length > 0}
          variant="contained"
        />
      </DialogActions>

      <DeleteDialog
        open={calcToDelete.length > 0}
        onClose={() => setCalcToDelete([])}
        onDelete={handleDeleteExamCalc}
      />

      {/* Edit Examination Calculation Dialog */}
      <Dialog open={editDialogOpen} maxWidth="md" fullWidth>
        <DialogTitle>Prüfverfahren bearbeiten</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {editingExamCalc && (
            <>
              <TextField
                size="small"
                name="name"
                label="Name"
                type="text"
                fullWidth
                value={editingExamCalc.name}
                onChange={(e) => {
                  setEditingExamCalc((prev: ExamCalc | null) =>
                    prev
                      ? {
                          ...prev,
                          name: e.target.value,
                        }
                      : null
                  )
                }}
                required
              />

              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editingExamCalc.isAccredited}
                      onChange={(e) => {
                        setEditingExamCalc((prev: ExamCalc | null) =>
                          prev
                            ? {
                                ...prev,
                                isAccredited: e.target.checked,
                              }
                            : null
                        )
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      Akkreditiert <HaufeIcon />
                    </Box>
                  }
                />
              </FormGroup>

              <Autocomplete
                size="small"
                options={treoLocationList}
                value={editingExamCalc.location}
                onChange={(_, value) => {
                  if (value) {
                    setEditingExamCalc((prev: ExamCalc | null) =>
                      prev
                        ? {
                            ...prev,
                            location: value,
                          }
                        : null
                    )
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        Standort <HaufeIcon />
                      </Box>
                    }
                  />
                )}
                renderOption={(props, option) => <li {...props}>{option.name}</li>}
                getOptionLabel={(option) => option.name}
              />

              <Typography variant="subtitle2" sx={{ mt: 2 }}>
                Kalkulation
              </Typography>
              <AddCalculation
                formState={editingExamCalc.calculation}
                setFormState={(value) => {
                  setEditingExamCalc((prev: ExamCalc | null) =>
                    prev
                      ? {
                          ...prev,
                          calculation: value,
                        }
                      : null
                  )
                }}
              />

              <Typography variant="subtitle2" sx={{ mt: 2 }}>
                Tags
              </Typography>
              <TagMultiSelect
                label="Tags"
                options={allTagOptions}
                value={editingExamCalc.tags || []}
                onChange={(next) => {
                  setEditingExamCalc((prev: ExamCalc | null) =>
                    prev
                      ? {
                          ...prev,
                          tags: next,
                        }
                      : null
                  )
                }}
                placeholder="Tag hinzufügen"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <StyledButton
            buttonType="cancel"
            onClick={() => setEditDialogOpen(false)}
            variant="contained"
          />
          <StyledButton
            buttonType="save"
            onClick={() => editingExamCalc && handleSaveExamCalc(editingExamCalc)}
            variant="contained"
          />
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}
